import React, { Component } from 'react';
import { connect } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Modal, Spinner, Table } from 'react-bootstrap';
import { createCheckoutSession, createPortalSession } from '@renenet/shared/src/actions/billing';
import Tier, { FREE_TIER_ASSET_LIMIT, FREE_TIER_LIABILITY_LIMIT, FREE_TIER_HISTORY_MONTHS } from '@renenet/shared/src/constants/tiers';
import TierBadge from '../TierBadge';

function mapStateToProps(state) {
  return { userInfo: state.userInfo };
}

function mapDispatchToProps(dispatch) {
  return {
    createCheckoutSession: () => dispatch(createCheckoutSession()),
    createPortalSession: () => dispatch(createPortalSession()),
  };
}

class BillingSectionBase extends Component {
  constructor(props) {
    super(props);
    this.state = {
      modalType: null,  // 'success' | 'cancelled' | null
      loading: false,
      error: false,
    };
  }

  componentDidMount() {
    const { searchParams, setSearchParams } = this.props;
    const payment = searchParams.get('payment');
    if (payment === 'success' || payment === 'cancelled') {
      this.setState({ modalType: payment });
      const next = new URLSearchParams(searchParams);
      next.delete('payment');
      setSearchParams(next, { replace: true });
    }
  }

  async handleCheckout() {
    this.setState({ loading: true, error: false });
    try {
      await this.props.createCheckoutSession();
    } catch (e) {
      console.error(e);
      this.setState({ loading: false, error: true });
    }
  }

  async handlePortal() {
    this.setState({ loading: true, error: false });
    try {
      await this.props.createPortalSession();
    } catch (e) {
      console.error(e);
      this.setState({ loading: false, error: true });
    }
  }

  render() {
    const { userInfo } = this.props;
    const { modalType, loading, error } = this.state;
    const isPremium = userInfo && userInfo.tier === Tier.PREMIUM;
    const hasStripeSubscription = isPremium && !!userInfo.stripeCustomerId;

    return (
      <div>
        <h4 className="mb-1">Billing</h4>
        <p className="text-muted mb-4">Manage your subscription plan.</p>

        <div className="mb-4">
          <p className="settings-field-label mb-1">Current Plan</p>
          {userInfo && <TierBadge tier={userInfo.tier} />}
        </div>

        {!isPremium && (
          <div>
            <Table bordered className="mb-4" style={{ maxWidth: '480px' }}>
              <thead>
                <tr>
                  <th style={{ width: '50%' }}></th>
                  <th className="text-center text-muted" style={{ width: '25%' }}>Free</th>
                  <th className="text-center text-primary" style={{ width: '25%' }}>Premium</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Assets</td>
                  <td className="text-center text-muted">Up to {FREE_TIER_ASSET_LIMIT}</td>
                  <td className="text-center text-primary fw-semibold">Unlimited</td>
                </tr>
                <tr>
                  <td>Liabilities</td>
                  <td className="text-center text-muted">Up to {FREE_TIER_LIABILITY_LIMIT}</td>
                  <td className="text-center text-primary fw-semibold">Unlimited</td>
                </tr>
                <tr>
                  <td>Net Worth History</td>
                  <td className="text-center text-muted">{FREE_TIER_HISTORY_MONTHS} months</td>
                  <td className="text-center text-primary fw-semibold">Full history</td>
                </tr>
                <tr>
                  <td>Currencies</td>
                  <td className="text-center text-muted">Fiat only</td>
                  <td className="text-center text-primary fw-semibold">Fiat + 800+ crypto</td>
                </tr>
                 <tr>
                  <td>FIRE Calculator</td>
                  <td className="text-center text-muted">✓</td>
                  <td className="text-center text-primary fw-semibold">✓</td>
                </tr>
                <tr>
                  <td>FIRE Projection</td>
                  <td className="text-center text-muted">✗</td>
                  <td className="text-center text-primary fw-semibold">✓</td>
                </tr>
                <tr>
                  <td>Net Worth History Import</td>
                  <td className="text-center text-muted">✗</td>
                  <td className="text-center text-primary fw-semibold">✓</td>
                </tr>
                <tr>
                  <td>Reminder Emails</td>
                  <td className="text-center text-muted">✗</td>
                  <td className="text-center text-primary fw-semibold">✓</td>
                </tr>
              </tbody>
            </Table>
            <Button
              variant="primary"
              onClick={() => this.handleCheckout()}
              disabled={loading}
            >
              {loading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
              Upgrade to Premium
            </Button>
          </div>
        )}

        {isPremium && hasStripeSubscription && (
          <div>
            {userInfo.stripeCancelAt ? (
              <p className="text-muted mb-3">
                Your Premium plan is active until{' '}
                <strong>{new Date(userInfo.stripeCancelAt * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</strong>.
                After that you'll be moved to the Free plan.
              </p>
            ) : (
              <p className="text-muted mb-3">
                You're on the Premium plan. Manage or cancel your subscription below.
              </p>
            )}
            <Button
              variant="secondary"
              onClick={() => this.handlePortal()}
              disabled={loading}
            >
              {loading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
              Manage Subscription
            </Button>
          </div>
        )}

        {error && (
          <Alert variant="danger" onClose={() => this.setState({ error: false })} dismissible>
            Something went wrong. Please try again later.
          </Alert>
        )}

        {isPremium && !hasStripeSubscription && (
          <p className="text-muted mb-0">
            Your Premium plan is managed by an administrator.
          </p>
        )}

        <Modal show={modalType !== null} onHide={() => this.setState({ modalType: null })} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {modalType === 'success' ? 'Subscription Updated' : 'No Changes Made'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalType === 'success' ? (
              <p className="text-success mb-0">You're now on Premium! Enjoy all premium features.</p>
            ) : (
              <p className="text-muted mb-0">Your subscription was not changed.</p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => this.setState({ modalType: null })}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}

const ConnectedBillingSection = connect(mapStateToProps, mapDispatchToProps)(BillingSectionBase);

function BillingSection(props) {
  const [searchParams, setSearchParams] = useSearchParams();
  return <ConnectedBillingSection {...props} searchParams={searchParams} setSearchParams={setSearchParams} />;
}

export default BillingSection;
